/*!

=========================================================
* Argon Dashboard React - v1.2.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
/* eslint-disable */
import { useEffect, useState } from 'react';
// javascipt plugin for creating charts
import Chart from 'chart.js';
// react plugin used to create charts
import { Bar, Line } from 'react-chartjs-2';
// reactstrap components
import { Card, CardBody, Container, Row, Col } from 'reactstrap';
import moment from 'moment';

// core components
import { chartOptions, parseOptions } from 'variables/charts.js';
import { Store } from 'StoreContext';
import OverViewTable from "../components/OverviewTable/Index"
import api from 'api';

import { chartRanges as sharedChartRanges, DEFAULT_RANGE, buildLabels, buildBarColors, buildPointConfig } from '../utils/chartHelpers';

const barOptions = {
  responsive: true,
  maintainAspectRatio: false,
  legend: { display: false },
  animation: { duration: 1500 },
  tooltips: {
    backgroundColor: '#1C1C1C',
    titleFontFamily: 'Outfit',
    bodyFontFamily: 'Outfit',
    cornerRadius: 8,
    xPadding: 12,
    yPadding: 10,
    callbacks: {
      label: function(t) { return t.yLabel.toLocaleString() + ' codes'; }
    }
  },
  scales: {
    xAxes: [{
      gridLines: { display: false },
      ticks: { padding: 8, fontFamily: 'Outfit', fontSize: 11, fontColor: '#888' },
      barThickness: 14,
    }],
    yAxes: [{
      gridLines: { display: true, lineWidth: 1, color: 'rgba(0,0,0,0.06)', drawBorder: false },
      ticks: {
        beginAtZero: true, padding: 10, maxTicksLimit: 5,
        fontFamily: 'Outfit', fontSize: 11, fontColor: '#888',
        callback: function(v) { return v.toLocaleString(); },
      },
    }],
  },
};

const lineOptions = {
  responsive: true,
  maintainAspectRatio: false,
  legend: { display: false },
  animation: { duration: 1500 },
  tooltips: {
    backgroundColor: '#1C1C1C',
    titleFontFamily: 'Outfit',
    bodyFontFamily: 'Outfit',
    cornerRadius: 8,
    xPadding: 12,
    yPadding: 10,
    callbacks: {
      label: function(t) { return t.yLabel.toLocaleString() + ' scans'; }
    }
  },
  scales: {
    xAxes: [{
      gridLines: { display: false },
      ticks: { padding: 8, fontFamily: 'Outfit', fontSize: 11, fontColor: '#888' },
    }],
    yAxes: [{
      gridLines: { display: true, lineWidth: 1, color: 'rgba(0,0,0,0.06)', drawBorder: false },
      ticks: {
        beginAtZero: true, padding: 10, maxTicksLimit: 5,
        fontFamily: 'Outfit', fontSize: 11, fontColor: '#888',
        callback: function(v) { return v.toLocaleString(); },
      },
    }],
  },
};

const Index = (props) => {
  const [codesData, setCodesData] = useState();
  const [scanData, setScanData] = useState();
  const [chartRange, setChartRange] = useState(DEFAULT_RANGE);
  const [chartsLoading, setChartsLoading] = useState(false);
  const [insights, setInsights] = useState([]);

  // SMS Usage state
  const [smsUsage, setSmsUsage] = useState([]);
  const [smsMonth, setSmsMonth] = useState(moment().format('YYYY-MM'));
  const [smsEditModal, setSmsEditModal] = useState(false);
  const [smsEditItem, setSmsEditItem] = useState(null);
  const [smsEditLimit, setSmsEditLimit] = useState('');
  const [smsEditRate, setSmsEditRate] = useState('');
  const [smsEditHardBlock, setSmsEditHardBlock] = useState(false);
  const [smsSaving, setSmsSaving] = useState(false);
  const [smsExpandedId, setSmsExpandedId] = useState(null);

  const store = Store();
  const { stats, user } = store;
  const { selectedBrandId } = store;

  const codesChartData = {
    labels: buildLabels(codesData),
    datasets: [{
      label: 'Codes Created',
      data: (codesData || []).map((item) => item.totalCodes),
      backgroundColor: buildBarColors(codesData),
      hoverBackgroundColor: 'rgba(54, 47, 217, 1)',
      borderRadius: 4,
    }],
  };

  const pointConfig = buildPointConfig(scanData);
  const scansChartData = {
    labels: buildLabels(scanData),
    datasets: [{
      label: 'Scans',
      data: (scanData || []).map((item) => item?.totalValidated),
      borderColor: 'rgba(54, 47, 216, 1)',
      backgroundColor: 'rgba(54, 47, 216, 0.08)',
      fill: true,
      tension: 0.4,
      ...pointConfig,
      pointHoverRadius: 5,
      borderWidth: 2.5,
    }],
  };

  useEffect(() => {
    setChartsLoading(true);
    Promise.all([
      api('get', `/codes/totalCodeAnalytics?range=${chartRange}`).then((res) => {
        setCodesData(res?.stats);
      }),
      api('get', `/codes/totalScanCodeAnalytics?range=${chartRange}`).then((res) => {
        setScanData(res?.stats);
      }),
    ]).catch(console.error).finally(() => setChartsLoading(false));
  }, [chartRange]);

  useEffect(() => {
    const brandParam = selectedBrandId ? `?brandId=${selectedBrandId}` : '';
    if (selectedBrandId) {
      api('get', `/insights${brandParam}`).then((res) => {
        setInsights(res?.insights || []);
      }).catch(() => setInsights([]));
    } else {
      setInsights([]);
    }
  }, [selectedBrandId]);

  // Fetch SMS usage
  useEffect(() => {
    api('get', '/users/sms-usage')
      .then((res) => { setSmsUsage(res?.usage || []); })
      .catch(console.error);
  }, []);

  // SMS month options (last 6 months)
  const smsMonthOptions = Array.from({ length: 6 }, (_, i) => {
    const m = moment().subtract(i, 'months');
    return { value: m.format('YYYY-MM'), label: m.format('MMMM YYYY') };
  });

  const filteredSmsUsage = smsUsage.filter((u) => u.month === smsMonth);

  const openSmsEdit = (item) => {
    setSmsEditItem(item);
    setSmsEditLimit(item.limit || 0);
    setSmsEditRate(item.overageRate || 0);
    setSmsEditHardBlock(item.hardBlock || false);
    setSmsEditModal(true);
  };

  const saveSmsEdit = () => {
    if (!smsEditItem) return;
    setSmsSaving(true);
    api('put', '/users/sms-usage', {
      brandId: smsEditItem.brandId?._id || smsEditItem.brandId,
      limit: Number(smsEditLimit),
      overageRate: Number(smsEditRate),
      hardBlock: smsEditHardBlock,
    })
      .then(() => {
        setSmsUsage((prev) =>
          prev.map((u) =>
            (u.brandId?._id || u.brandId) === (smsEditItem.brandId?._id || smsEditItem.brandId) && u.month === smsEditItem.month
              ? { ...u, limit: Number(smsEditLimit), overageRate: Number(smsEditRate), hardBlock: smsEditHardBlock }
              : u
          )
        );
        setSmsEditModal(false);
      })
      .catch(console.error)
      .finally(() => setSmsSaving(false));
  };

  if (window.Chart) {
    parseOptions(Chart, chartOptions());
  }

  return (
    <>
      <Container style={{ fontFamily: "Outfit" }} fluid>
        <h2 className="vs-section-title" style={{ marginBottom: '16px' }}>Key Analytics</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '-12px', marginBottom: '20px', flexWrap: 'wrap' }}>
          {sharedChartRanges.map((r) => (
            <button
              key={r.value}
              onClick={() => setChartRange(r.value)}
              style={{
                fontFamily: 'Outfit',
                fontWeight: 600,
                fontSize: '13px',
                backgroundColor: chartRange === r.value ? '#362FD8' : '#F0F0F0',
                color: chartRange === r.value ? '#fff' : '#555',
                border: 'none',
                borderRadius: '6px',
                padding: '5px 14px',
                cursor: 'pointer',
              }}
            >
              {r.label}
            </button>
          ))}
          <span style={{ color: '#aaa', fontSize: '12px', marginLeft: '4px' }}>* current month (partial)</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px' }}>
          <Card className='shadow' style={{ borderRadius: '12px', border: 'none', position: 'relative' }}>
            {chartsLoading && (
              <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(255,255,255,0.7)', zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '12px' }}>
                <div style={{ width: 24, height: 24, border: '3px solid #362FD8', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
              </div>
            )}
            <div style={{ paddingBlock: '1.25rem' }} className='bg-transparent px-4'>
              <h2 style={{ color: '#888', fontSize: '12px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }} className='mb-1'>Total Scans</h2>
              <h2 style={{ color: '#1C1C1C', fontSize: '28px', fontWeight: 700 }} className='mb-0'>{(scanData || []).reduce((sum, i) => sum + (i.totalValidated || 0), 0).toLocaleString()}</h2>
            </div>
            <CardBody style={{ paddingTop: 0 }}>
              <div style={{ height: "220px" }} className='chart'>
                <Line data={scansChartData} options={lineOptions} />
              </div>
            </CardBody>
          </Card>
          <Card className='shadow' style={{ borderRadius: '12px', border: 'none', position: 'relative' }}>
            {chartsLoading && (
              <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(255,255,255,0.7)', zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '12px' }}>
                <div style={{ width: 24, height: 24, border: '3px solid #362FD8', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
              </div>
            )}
            <div style={{ paddingBlock: '1.25rem' }} className='bg-transparent px-4'>
              <h2 style={{ color: '#888', fontSize: '12px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }} className='mb-1'>Codes Created</h2>
              <h2 style={{ color: '#1C1C1C', fontSize: '28px', fontWeight: 700 }} className='mb-0'>{(codesData || []).reduce((sum, i) => sum + (i.totalCodes || 0), 0).toLocaleString()}</h2>
            </div>
            <CardBody style={{ paddingTop: 0 }}>
              <div style={{ height: "220px" }} className='chart'>
                <Bar data={codesChartData} options={barOptions} />
              </div>
            </CardBody>
          </Card>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        {/* ─── Brand Insights (admin: shown when a brand is selected) ─── */}
        {insights.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <h2 className="vs-section-title" style={{ marginBottom: '8px' }}>Insights</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '10px' }}>
              {insights.map((ins, idx) => {
                const iconMap = { clock: '', trending: '', globe: '' };
                const colorMap = { peak_scan_time: '#362FD8', batch_performance: '#00B207', geographic_anomaly: '#dc3545' };
                const color = colorMap[ins.type] || '#362FD8';
                const batchDetail = ins.type === 'batch_performance' && ins.details?.[0]
                  ? `#${ins.details[0].batchId} · ${ins.details[0].createdAt ? new Date(ins.details[0].createdAt).toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: '2-digit'}) : ''}`
                  : null;
                return (
                  <div key={idx} style={{
                    backgroundColor: '#fff', borderRadius: '10px', padding: '14px 16px',
                    fontFamily: 'Outfit', borderLeft: `3px solid ${color}`,
                    boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                  }}>
                    <div style={{ marginBottom: '4px' }}>
                      <span style={{ fontSize: '11px', fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.3px' }}>{ins.title}</span>
                    </div>
                    <div style={{ fontSize: '14px', fontWeight: 700, color, marginBottom: '2px' }}>{ins.metric}</div>
                    {batchDetail && <div style={{ fontSize: '11px', color: '#999', marginBottom: '2px' }}>{batchDetail}</div>}
                    <p style={{ fontSize: '11px', color: '#888', lineHeight: '1.4', margin: '0 0 2px' }}>{ins.description}</p>
                    {ins.actionText && <span style={{ fontSize: '11px', color: '#362FD8', fontWeight: 500 }}>{ins.actionText}</span>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <Row>
          <Col>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h2 className="vs-section-title mb-0">Activity Overview</h2>
              <button className="vs-btn-create" style={{ padding: '7px 14px', fontSize: '13px' }} onClick={() => window.location.href = '/admin/userlogs'}>See all</button>
            </div>
            <OverViewTable />
          </Col>
        </Row>

        {/* ─── SMS Usage Section ─── */}
        <div style={{ marginTop: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px', marginBottom: '12px' }}>
            <h2 className="vs-section-title" style={{ margin: 0 }}>SMS Usage</h2>
            <select
              value={smsMonth}
              onChange={(e) => setSmsMonth(e.target.value)}
              style={{
                fontFamily: 'Outfit',
                fontSize: '13px',
                fontWeight: 500,
                padding: '6px 12px',
                borderRadius: '6px',
                border: '1px solid #ddd',
                backgroundColor: '#fff',
                cursor: 'pointer',
              }}
            >
              {smsMonthOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {/* Desktop table */}
          <div className="d-none d-md-block" style={{ backgroundColor: '#fff', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: '16px' }}>
            <table style={{ width: '100%', fontFamily: 'Outfit', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <th style={{ padding: '12px 10px', textAlign: 'left', fontSize: '13px', fontWeight: 600, color: '#1C1C1C', textTransform: 'capitalize', letterSpacing: 0, whiteSpace: 'nowrap' }}>Brand</th>
                  <th style={{ padding: '12px 10px', textAlign: 'center', fontSize: '13px', fontWeight: 600, color: '#1C1C1C', textTransform: 'capitalize', letterSpacing: 0, whiteSpace: 'nowrap' }}>Sent / Limit</th>
                  <th style={{ padding: '12px 10px', textAlign: 'center', fontSize: '13px', fontWeight: 600, color: '#1C1C1C', textTransform: 'capitalize', letterSpacing: 0, whiteSpace: 'nowrap' }}>Overage</th>
                  <th style={{ padding: '12px 10px', textAlign: 'center', fontSize: '13px', fontWeight: 600, color: '#1C1C1C', textTransform: 'capitalize', letterSpacing: 0, whiteSpace: 'nowrap' }}>Overage Cost</th>
                  <th style={{ padding: '12px 10px', textAlign: 'center', fontSize: '13px', fontWeight: 600, color: '#1C1C1C', textTransform: 'capitalize', letterSpacing: 0, whiteSpace: 'nowrap' }}>Mode</th>
                  <th style={{ padding: '12px 10px', textAlign: 'center', fontSize: '13px', fontWeight: 600, color: '#1C1C1C', textTransform: 'capitalize', letterSpacing: 0, whiteSpace: 'nowrap' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredSmsUsage.length === 0 ? (
                  <tr>
                    <td colSpan="6" style={{ padding: '24px', textAlign: 'center', color: '#999', fontFamily: 'Outfit', fontSize: '14px', border: 'none' }}>No SMS usage data for this month</td>
                  </tr>
                ) : filteredSmsUsage.map((item, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid #f5f5f5' }}>
                    <td style={{ padding: '10px', fontWeight: 500, fontSize: '14px' }}>{item.brandId?.name || 'Unknown'}</td>
                    <td style={{ padding: '10px', textAlign: 'center', fontSize: '14px' }}>
                      <span style={{ fontWeight: 600 }}>{item.sent}</span>
                      <span style={{ color: '#999' }}> / {item.limit}</span>
                    </td>
                    <td style={{ padding: '10px', textAlign: 'center', fontSize: '14px', color: item.overageCount > 0 ? '#dc3545' : '#888' }}>{item.overageCount || 0}</td>
                    <td style={{ padding: '10px', textAlign: 'center', fontSize: '14px', fontWeight: 500, color: item.overageCost > 0 ? '#dc3545' : '#888' }}>${((item.overageCost || 0) / 100).toFixed(2)}</td>
                    <td style={{ padding: '10px', textAlign: 'center' }}>
                      <span style={{
                        fontSize: '11px',
                        fontWeight: 600,
                        padding: '3px 10px',
                        borderRadius: '4px',
                        backgroundColor: item.hardBlock ? '#dc3545' : '#00B207',
                        color: '#fff',
                      }}>
                        {item.hardBlock ? 'Hard Block' : 'Soft Block'}
                      </span>
                    </td>
                    <td style={{ padding: '10px', textAlign: 'center' }}>
                      <button
                        className="vs-btn-modal-action"
                        onClick={() => openSmsEdit(item)}
                        style={{ fontSize: '12px', padding: '4px 12px' }}
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile card view */}
          <div className="d-block d-md-none" style={{ backgroundColor: '#fff', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: '16px' }}>
            {filteredSmsUsage.length === 0 ? (
              <div style={{ padding: '24px', textAlign: 'center', color: '#999', fontFamily: 'Outfit', fontSize: '14px' }}>No SMS usage data for this month</div>
            ) : filteredSmsUsage.map((item, idx) => {
              const isExpanded = smsExpandedId === (item.brandId?._id || idx);
              return (
                <div
                  key={idx}
                  style={{
                    padding: '12px 16px',
                    borderBottom: '1px solid #f0f0f0',
                    borderLeft: `4px solid ${item.hardBlock ? '#dc3545' : '#362FD8'}`,
                    cursor: 'pointer',
                  }}
                  onClick={() => setSmsExpandedId(isExpanded ? null : (item.brandId?._id || idx))}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '4px' }}>
                    <span style={{ fontSize: '14px', fontWeight: 600, fontFamily: 'Outfit', color: '#1C1C1C' }}>{item.brandId?.name || 'Unknown'}</span>
                    <span style={{
                      fontSize: '11px',
                      fontWeight: 600,
                      padding: '2px 8px',
                      borderRadius: '4px',
                      backgroundColor: item.hardBlock ? '#dc3545' : '#00B207',
                      color: '#fff',
                    }}>
                      {item.hardBlock ? 'Hard' : 'Soft'}
                    </span>
                  </div>
                  <p style={{ fontSize: '13px', color: '#555', margin: 0, fontFamily: 'Outfit' }}>
                    {item.sent} / {item.limit} sent
                  </p>
                  {isExpanded && (
                    <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #f0f0f0' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '4px' }}>
                        <span style={{ color: '#888' }}>Overage count</span>
                        <span style={{ fontWeight: 500, color: item.overageCount > 0 ? '#dc3545' : '#333' }}>{item.overageCount || 0}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '8px' }}>
                        <span style={{ color: '#888' }}>Overage cost</span>
                        <span style={{ fontWeight: 500, color: item.overageCost > 0 ? '#dc3545' : '#333' }}>${((item.overageCost || 0) / 100).toFixed(2)}</span>
                      </div>
                      <button
                        className="vs-btn-modal-action"
                        onClick={(e) => { e.stopPropagation(); openSmsEdit(item); }}
                        style={{ fontSize: '12px', padding: '4px 12px', width: '100%' }}
                      >
                        Edit
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* SMS Edit Modal */}
        {smsEditModal && smsEditItem && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1050,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backgroundColor: 'rgba(0,0,0,0.5)',
          }} onClick={() => setSmsEditModal(false)}>
            <div style={{
              backgroundColor: '#fff', borderRadius: '12px', padding: '24px', width: '100%', maxWidth: '420px',
              fontFamily: 'Outfit', boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
            }} onClick={(e) => e.stopPropagation()}>
              <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#1C1C1C', marginBottom: '4px' }}>Edit SMS Usage</h3>
              <p style={{ fontSize: '13px', color: '#888', marginBottom: '20px' }}>{smsEditItem.brandId?.name || 'Brand'}</p>

              <div style={{ marginBottom: '14px' }}>
                <label style={{ fontSize: '13px', fontWeight: 500, color: '#555', display: 'block', marginBottom: '4px' }}>Monthly Limit</label>
                <input
                  type="number"
                  value={smsEditLimit}
                  onChange={(e) => setSmsEditLimit(e.target.value)}
                  style={{
                    width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #ddd',
                    fontFamily: 'Outfit', fontSize: '14px',
                  }}
                />
              </div>

              <div style={{ marginBottom: '14px' }}>
                <label style={{ fontSize: '13px', fontWeight: 500, color: '#555', display: 'block', marginBottom: '4px' }}>Overage Rate (cents per SMS)</label>
                <input
                  type="number"
                  value={smsEditRate}
                  onChange={(e) => setSmsEditRate(e.target.value)}
                  style={{
                    width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #ddd',
                    fontFamily: 'Outfit', fontSize: '14px',
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={smsEditHardBlock}
                  onChange={(e) => setSmsEditHardBlock(e.target.checked)}
                  style={{ width: '16px', height: '16px', accentColor: '#362FD8' }}
                />
                <label style={{ fontSize: '13px', fontWeight: 500, color: '#555', margin: 0 }}>Hard Block (stop sending when limit reached)</label>
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  className="vs-btn-cancel"
                  onClick={() => setSmsEditModal(false)}
                  style={{ padding: '8px 18px', fontSize: '13px' }}
                >
                  Cancel
                </button>
                <button
                  className="vs-btn-save"
                  onClick={saveSmsEdit}
                  disabled={smsSaving}
                  style={{ padding: '8px 18px', fontSize: '13px', opacity: smsSaving ? 0.6 : 1 }}
                >
                  {smsSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}

      </Container>
    </>
  );
};

export default Index;
